// src/pages/Dashboard.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { getDashboard } from '../utils/api';
import { StatusBadge, formatDate, getTodayStr } from '../components/UI';
import { useLeadLookups } from '../hooks/useLeadLookups';

ChartJS.register(ArcElement, Tooltip, Legend);

export default function Dashboard({ onAddLead }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { statuses } = useLeadLookups();
  const navigate = useNavigate();

  const load = useCallback(() => {
    getDashboard()
      .then(r => setData(r.data))
      .catch(() => { })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); const iv = setInterval(load, 60000); return () => clearInterval(iv); }, [load]);

  if (loading) return <div style={{ padding: 40, color: 'var(--gray-400)' }}>Loading dashboard...</div>;
  if (!data) return <div style={{ padding: 40, color: 'var(--red)' }}>Failed to load dashboard. Please refresh.</div>;

  const { stats, today_followups, overdue_followups, pie, service_breakdown, recent_leads } = data;
  const allDue = [...(overdue_followups || []), ...(today_followups || [])];
  const dueDisplayLimit = 10;
  const dueDisplayed = allDue.slice(0, dueDisplayLimit);
  const services = service_breakdown || [];
  const maxServiceCount = Math.max(1, ...services.map(s => s.cnt));
  const totalServiceLeads = services.reduce((a, s) => a + s.cnt, 0) || 1;
  // pie is now [{status, color, count}, ...] driven by lead_statuses (Settings > Lead Pipeline)
  const pieRows = pie || [];
  const pieLabels = pieRows.map(p => p.status);
  const pieValues = pieRows.map(p => p.count);
  const pieColors = pieRows.map(p => p.color);
  const today = getTodayStr();

  const goLeads = (state) => navigate('/leads', { state });

  return (
    <div>
      {stats.pending_fu > 0 && (
        <div className="alert-banner">
          <i className="ti ti-bell-ringing" />
          <div>
            <strong>You have {stats.pending_fu} follow-up{stats.pending_fu !== 1 ? 's' : ''} due today or overdue</strong>
            {' — '}
            <span style={{ color: 'var(--primary)', cursor: 'pointer', textDecoration: 'underline' }} onClick={() => navigate('/followups')}>View all follow-ups →</span>
          </div>
        </div>
      )}

      <div className="stats-grid">
        <div className="stat-card c-blue" style={{ cursor: 'pointer' }} onClick={() => goLeads({ datePreset: 'today' })}>
          <div className="stat-icon"><i className="ti ti-sparkles" /></div>
          <div className="stat-label">Today's New Leads</div>
          <div className="stat-value">{stats.today}</div>
          <div className="stat-sub">added today ↗</div>
        </div>

        <div className="stat-card c-amber" style={{ cursor: 'pointer' }} onClick={() => navigate('/followups')}>
          <div className="stat-icon"><i className="ti ti-calendar-event" /></div>
          <div className="stat-label">Pending Follow-ups</div>
          <div className="stat-value">{stats.pending_fu}</div>
          <div className="stat-sub">due today & overdue ↗</div>
        </div>

        <div className="stat-card c-purple" style={{ cursor: 'pointer' }} onClick={() => goLeads({ datePreset: 'month' })}>
          <div className="stat-icon"><i className="ti ti-calendar-stats" /></div>
          <div className="stat-label">This Month</div>
          <div className="stat-value">{stats.month}</div>
          <div className="stat-sub">total leads ↗</div>
        </div>

        <div className="stat-card c-teal" style={{ cursor: 'pointer' }} onClick={() => goLeads({ datePreset: 'fy' })}>
          <div className="stat-icon"><i className="ti ti-chart-bar" /></div>
          <div className="stat-label">This FY</div>
          <div className="stat-value">{stats.fy}</div>
          <div className="stat-sub">Apr–Mar ↗</div>
        </div>

        <div className="stat-card c-green" style={{ cursor: 'pointer' }} onClick={() => goLeads({ status: 'Converted' })}>
          <div className="stat-icon"><i className="ti ti-circle-check" /></div>
          <div className="stat-label">Converted</div>
          <div className="stat-value">{stats.converted}</div>
          <div className="stat-sub">total clients ↗</div>
        </div>
      </div>

      <div className="dashboard-grid">
        <div>
          <div className="card">
            <div className="card-title">
              Today's Follow-ups
              <span style={{ fontSize: 12, color: 'var(--gray-400)', fontWeight: 400 }}>{allDue.length} due</span>
            </div>
            {allDue.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--gray-400)' }}>
                <i className="ti ti-check" style={{ fontSize: 24, color: 'var(--green)', display: 'block', marginBottom: 6 }} />
                No follow-ups due today. Great work!
              </div>
            ) : (
              <>
                {dueDisplayed.map(l => (
                  <div key={l.id} className="fu-item" onClick={() => navigate(`/leads/${l.id}`)}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: l.followup_date < today ? 'var(--red)' : 'var(--amber)', flexShrink: 0, marginTop: 5 }} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--black)' }}>{l.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 2 }}>{l.company} · {l.service}</div>
                      {l.followup_date < today && <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--red)', marginTop: 2 }}>Overdue: {formatDate(l.followup_date)}</div>}
                    </div>
                    <StatusBadge status={l.status} statuses={statuses} />
                  </div>
                ))}
                {allDue.length > dueDisplayLimit && (
                  <div style={{ textAlign: 'center', marginTop: 10, paddingTop: 10, borderTop: '0.5px solid var(--gray-100)' }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--primary)', cursor: 'pointer', textDecoration: 'underline' }} onClick={() => navigate('/followups')}>
                      View all {allDue.length} follow-ups →
                    </span>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <div className="dashboard-side-grid">
          <div className="card">
            <div className="card-title">Leads by Service</div>
            {services.length > 0 ? (
              <div className="svc-list">
                {services.map(s => {
                  const clickable = s.service !== 'Unspecified';
                  const pct = Math.round((s.cnt / totalServiceLeads) * 100);
                  return (
                    <div
                      key={s.service}
                      className={`svc-row${clickable ? ' clickable' : ''}`}
                      onClick={clickable ? () => goLeads({ service: s.service }) : undefined}
                    >
                      <div className="svc-row-top">
                        <span className="svc-name">{s.service}</span>
                        <span className="svc-count" title={`${s.cnt} lead${s.cnt !== 1 ? 's' : ''}`}>{pct}%</span>
                      </div>
                      <div className="svc-bar-track">
                        <div className="svc-bar-fill" style={{ width: `${(s.cnt / maxServiceCount) * 100}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ textAlign: 'center', color: 'var(--gray-400)', padding: '20px 0', fontSize: 13 }}>No leads yet</div>
            )}
          </div>
          <div className="card">
            <div className="card-title">Lead Status Breakdown</div>
            {pieValues.reduce((a, b) => a + b, 0) > 0 ? (
              <>
                <Doughnut
                  data={{ labels: pieLabels, datasets: [{ data: pieValues, backgroundColor: pieColors, borderWidth: 0, hoverOffset: 4 }] }}
                  options={{ responsive: true, cutout: '60%', plugins: { legend: { display: false }, tooltip: { callbacks: { label: c => ` ${c.label}: ${c.raw}` } } } }}
                />
                <div className="legend">
                  {pieLabels.map((l, i) => (
                    <div key={l} className="legend-item" onClick={() => goLeads({ status: l })}>
                      <div className="legend-dot" style={{ background: pieColors[i] }} />
                      <span style={{ flex: 1 }}>{l}</span>
                      <span style={{ fontWeight: 700, color: 'var(--black)' }}>{pieValues[i]}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div style={{ textAlign: 'center', color: 'var(--gray-400)', padding: '20px 0', fontSize: 13 }}>No leads yet</div>
            )}
          </div>

          <div className="card" style={{ gridColumn: '1 / -1' }}>
            <div className="card-title">Recent Leads</div>
            {(recent_leads || []).length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--gray-400)' }}>No leads yet</div>
            ) : (
              (recent_leads || []).map(l => (
                <div key={l.id} onClick={() => navigate(`/leads/${l.id}`)} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: '6px 0', borderBottom: '0.5px solid var(--gray-100)' }}>
                  <div className="avatar" style={{ background: statuses.find(s => s.name === l.status)?.color || 'var(--gray-400)', width: 28, height: 28, fontSize: 10 }}>
                    {l.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--black)' }}>{l.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--gray-400)' }}>{l.service || l.company}</div>
                  </div>
                  <StatusBadge status={l.status} statuses={statuses} />
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
